import styled from '@emotion/styled';
import { Constants } from '@seedlang/constants';
import { Languages } from '@seedlang/constants/src';
import { ConceptWordStore, LanguageStore, LevelStore, RouteStore, SentenceStore, SentenceSubmissionStore, WordConceptStore, WordSentenceStore, WordStore, WordStructureStore, WordTypeStore } from '@seedlang/stores';
import { isBlank, isPresent } from '@seedlang/utils';
import autobind from 'autobind-decorator';
import Alert from 'components/alert';
import ButtonPrimary from 'components/button/button_primary';
import DeleteButton from 'components/button/delete_button';
import GrayButton from 'components/button/gray_button';
import SquareButton from 'components/button/square_button';
import CheckIfTrue from 'components/check_if_true';
import Copy from 'components/copy';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceSelect from 'components/form/in_place_select';
import InPlaceText from 'components/form/in_place_text';
import InPlaceVideoUpload from 'components/form/in_place_video_upload';
import InfoTooltip from 'components/info_tooltip';
import Grid from 'components/layout/grid';
import Stack from 'components/layout/stack';
import Modal from 'components/modal';
import Paginator from 'components/paginator';
import Spinner from 'components/spinner';
import Text from 'components/text';
import WordSearch from 'components/word_search';
import { orderBy } from 'lodash';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import ConceptWordIndex from 'pages/builder/concept_words/concept_word_index';
import SentenceSubmissionIndex from 'pages/builder/sentence_submissions/sentence_submission_index';
import WordSourceEdit from 'pages/builder/sources/word_source_edit';
import ExcludeEdit from 'pages/builder/words/exclude_edit';
import MeaningsEdit from 'pages/builder/words/meanings_edit';
import RelatedWordsEdit from 'pages/builder/words/related_words_edit';
import SynonymsEdit from 'pages/builder/words/synonyms_edit';
import React from 'react';
import { Link } from 'react-router';
import ReactTooltip from 'react-tooltip';
import GenderVariantEdit from './gender_variant_edit';

const ReviewedUser = styled.div`
  font-size: 12px;
  padding-left: 18px;
  background: #FFF;
`;

const ConjugatedVerbStringGrouping = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

@observer
class WordEdit extends React.Component {
  @observable showNote = false;
  @observable editableConjugations = false;
  @observable showConceptModal = false;
  @observable unknownComponents = [];
  @observable showFormToCreateNewWordStructure = false;
  @observable mergeSpinner = false;
  @observable mergeMessage;
  @observable showWorksheets = false;

  @computed get noun() {
    return WordStore.showData.hasWordType && WordStore.showData.wordType.abbreviation === 'noun';
  }

  @computed get pronoun() {
    return WordStore.showData.hasWordType && WordStore.showData.wordType.abbreviation === 'pron';
  }

  @computed get verb() {
    return WordStore.showData.hasWordType && WordStore.showData.wordType.abbreviation === 'vrb';
  }

  @computed get adverb() {
    return WordStore.showData.hasWordType && WordStore.showData.wordType.abbreviation === 'adv';
  }

  @computed get adj() {
    return WordStore.showData.hasWordType && WordStore.showData.wordType.abbreviation === 'adj';
  }

  @computed get number() {
    return WordStore.showData.hasWordType && WordStore.showData.wordType.abbreviation === 'num';
  }

  constructor(props) {
    super(props);
    this.getWordShow();
    WordSentenceStore.getIndex({ sort: 'level_abbreviation, target_text', queryStrings: { expanded: true, back_end: true }, ids: { wordId: this.props.params.wordId } });
    WordConceptStore.getIndex({ ids: { wordId: this.props.params.wordId } });
    if (!LevelStore.hasIndexData) {
      LevelStore.getIndex();
    }
  }

  @autobind getWordShow() {
    WordStore.getShow({ queryStrings: { expand_translation_sources: true, expand_worksheets: true }, ids: { wordId: this.props.params.wordId } }, this.afterWordGetShow);
  }

  @autobind afterWordGetShow(resp) {
    WordStore.setShowData(resp);
    if (resp.root && resp.wordTypeId === '4c4389c1-f805-4d1c-ba45-6721cf400aef') {
      LanguageStore.getShow({ ids: { languageId: resp.languageId } });
    }
  }

  componentWillUnmount() {
    WordStore.clearShowData();
  }

  componentDidUpdate(prevProps) {
    if (this.props.params.wordId !== prevProps.params.wordId) {
      SentenceSubmissionStore.getIndex({ ids: { wordId: this.props.params.wordId } });
      WordStore.getShow({ ids: { wordId: this.props.params.wordId } });
      WordSentenceStore.getIndex({ ids: { wordId: this.props.params.wordId } });
      WordConceptStore.getIndex({ ids: { wordId: this.props.params.wordId } });
    }
  }

  @autobind afterChange() {
    WordStore.reloadShow();
  }

  @autobind onChangeConcepts() {
    this.afterChange();
    WordConceptStore.getIndex({ ids: { wordId: this.props.params.wordId } });
  }

  @autobind clearRoot() {
    WordStore.update({ data: { root_word_id: null }, ids: { wordId: this.props.params.wordId } }, this.afterChange);
  }

  @autobind togglePluralNoun(wordId) {
    if (WordStore.showData?.pluralNouns.map(item => item.id).includes(wordId)) {
      WordStore.update({ data: { singular_noun_id: null }, ids: { wordId: wordId } }, this.afterChange);
    } else {
      WordStore.update({ data: { singular_noun_id: this.props.params.wordId }, ids: { wordId: wordId } }, this.afterChange);
    }
  }

  @autobind createFeminineForm(targetText) {
    const feminineForm = WordStore.showData.childWords.filter(word => word.targetText === targetText)[0];
    if (feminineForm) {
      WordStore.update({ ids: { wordId: WordStore.showData.id }, data: { gender_variant_id: feminineForm.id } }, this.afterChange);
    } else {
      WordStore.create({
        data: {
          root: false,
          target_text: targetText,
          root_word_id: WordStore.showData.id,
          language_id: WordStore.showData.languageId,
          root_word_type_id: WordStore.showData.rootWordType?.id,
          word_type_id: WordStore.showData.wordType.id,
        },
      }, this.afterCreateFeminineForm);
    }
  }

  @autobind afterCreateFeminineForm(resp) {
    WordStore.update({ ids: { wordId: WordStore.showData.id }, data: { gender_variant_id: resp.word.id } }, this.afterChange);
  }

  @autobind onSelectPlural(word) {
    WordStore.update({ ids: { wordId: word.id }, data: { root_word_id: WordStore.showData.id } }, this.afterChange);
  }

  @autobind onCreatePlural(targetText) {
    WordStore.createPlural({ ids: { wordId: WordStore.showData.id }, data: { target_text: targetText } }, this.afterCreatePlural);
  }

  @autobind afterCreatePlural(resp) {
    RouteStore.routeToNamed('builder.words.edit', { wordId: resp.word.id });
  }

  @autobind onMergeDuplicateWords() {
    this.mergeSpinner = true;
    this.mergeMessage = 'Merging... This may take a few minutes. You will be notified on Slack #didactic-alerts when it\'s done.';
    WordStore.mergeDuplicateWords({ ids: { wordId: WordStore.showData.id } }, this.afterMergeDuplicateWords);
  }

  @autobind afterMergeDuplicateWords(resp) { // link to merged word
    this.mergeSpinner = false;
    if (resp.didMerge) {
      if (resp.wordId !== WordStore.showData.id) {
        this.mergeMessage = 'The current word was merged and destroyed. Rerouting you to merged word...';
        RouteStore.routeToNamed('builder.words.edit', { wordId: resp.wordId });
      } else {
        this.mergeMessage = 'Merge was successful. Duplicates were destroyed.';
      }
    } else {
      this.mergeMessage = 'No merging was performed';
    }
  }

  @autobind createNewWordStructure() {
    if (isPresent(this.refs.text.value)) {
      WordStructureStore.create({
        data: {
          text: this.refs.text.value,
          sourceText: this.refs.sourceText.value,
          wordId: WordStore.showData?.id,
        },
      }
        , this.afterUpdateNewWordStructure);
    }
  }

  @autobind afterUpdateNewWordStructure(resp) {
    if (resp.success) {
      this.showFormToCreateNewWordStructure = false;
    }
    this.afterChange();
    this.unknownComponents = resp.unknownComponents;
  }

  @autobind deleteWordStructure(wordStructureId) {
    WordStructureStore.destroy({ ids: { wordStructureId: wordStructureId } }, this.afterChange);
  }

  @computed get levelOptions() {
    if (!LevelStore.hasIndexData || !WordStore.hasShowData) { return; }
    const options = LevelStore.indexData.filter(level => isPresent(level.abbreviation) && level.abbreviation !== 'N').map(level => [level.id, level.abbreviation]);
    if (WordStore.showData.wordType?.name === 'Number') {
      return options.slice(0, 2);
    } return options;
  }

  @computed get worksheets() {
    if (this.showWorksheets) {
      return WordStore.showData.worksheets;
    } return WordStore.showData.worksheets.slice(0, 2);
  }

  @autobind createDuplicateTarget() {
    if (WordStore.showData?.target?.translationsCount > 1) {
      WordStore.createDuplicateTarget({ ids: { wordId: WordStore.showData.id } }, this.getWordShow);
    }
  }

  @autobind onDelete() {
    WordStore.destroy({ ids: { wordId: WordStore.showData.id } }, () => RouteStore.routeToNamed('builder.words.index', { page: 1 }));
  }

  @autobind rebuildFalseMultipleChoiceOptions() {
    WordStore.updateFalseMultipleChoiceOptions({ ids: { wordId: WordStore.showData.id } }, this.getWordShow);
  }

  @autobind onRemoveConcept(conceptId) {
    ConceptWordStore.destroy({ ids: { concept_id: conceptId, word_id: WordStore.showData.id } }, this.getWordShow);
  }

  _content() {
    if (WordStore.hasShowData) {
      return (
        <Stack gap='sm' alignItems='stretch'>
          {
            this.showNote &&
            <Modal
              onCloseModal={() => this.showNote = false}
            >
              <fieldset>
                <legend>Note</legend>
                <InPlaceText
                  defaultValue={WordStore.showData.note}
                  model='words'
                  inputType='textarea'
                  field='note'
                  id={WordStore.showData.id}
                  afterChange={this.afterChange}
                />
              </fieldset>
            </Modal>
          }
          {
            this.showConceptModal &&
            <ConceptWordIndex
              hideWordCard
              onCloseModal={() => this.showConceptModal = false}
              word={WordStore.showData}
              onChange={this.onChangeConcepts}
            />
          }
          <Grid columns={2}>
            <fieldset>
              <legend>Video Clips</legend>
              {
                WordStore.showData.target && WordStore.showData.target.videoClips.map(item => {
                  return (
                    <div
                      key={item.id}
                    >
                      <InPlaceVideoUpload
                        model='video_clips'
                        videoClip={item}
                        onChange={this.afterChange}
                        querystring={{
                          videoable_id: WordStore.showData.target && WordStore.showData.target.id,
                          videoable_type: 'Target',
                        }}
                      />
                    </div>
                  );
                })
              }
              {
                isBlank(WordStore.showData?.target?.videoClips) && WordStore.showData?.target &&
                <div>
                  <InPlaceVideoUpload
                    model='video_clips'
                    removeModel='target'
                    removeId={WordStore.showData.target.id}
                    videoClip={WordStore.showData.target.videoClip}
                    onChange={this.afterChange}
                    querystring={{
                      videoable_id: WordStore.showData.target.id,
                      videoable_type: 'Target',
                    }}
                  />
                </div>
              }
              <div style={{ justifyContent: 'space-between' }}>
                <div>
                  {
                    WordStore.showData?.target?.id && isPresent(WordStore.showData?.target?.videoClips) &&
                    <div>
                      <InPlaceCheckbox
                        model='targets'
                        id={WordStore.showData.target.id}
                        value={WordStore.showData.target.replaceVideo}
                        field='replace_video'
                      >
                        Replace Video
                      </InPlaceCheckbox>
                    </div>
                  }
                  <div>
                    <InPlaceCheckbox
                      model='words'
                      id={WordStore.showData.id}
                      value={WordStore.showData.needsSecondaryVideo}
                      field='needs_secondary_video'
                    >
                      Needs Secondary Video
                    </InPlaceCheckbox>
                  </div>
                </div>
                {
                  WordStore.showData?.target?.id && isPresent(WordStore.showData?.target?.videoClips) && WordStore.showData.allowDuplicateRoots &&
                  <Text fontSize='14px'>
                    <ButtonPrimary
                      disabled={WordStore.showData?.target?.translationsCount < 2}
                      onClick={this.createDuplicateTarget}
                    >
                      Create Duplicate Target
                      <InfoTooltip
                        color={WordStore.showData?.target?.translationsCount < 2 ? 'gray' : null}
                        position='right'
                      >
                        <div style={{ width: '400px', textAlign: 'left', textWrap: 'auto' }}>Use this option if this word has a duplicate and their pronunciations are different.</div>
                        {
                          WordStore.showData?.target?.translationsCount < 2 &&
                          <div style={{ width: '100%', textAlign: 'left' }}>[Disabled because this target is only used for this word.]</div>
                        }
                      </InfoTooltip>
                    </ButtonPrimary>
                  </Text>
                }
              </div>
            </fieldset>
            <fieldset>
              <legend>Language</legend>
              {WordStore.hasShowData && Constants.LANGUAGE_OPTIONS[WordStore.showData.languageId]}
            </fieldset>
            <fieldset>
              <legend>Creation</legend>
              {`${WordStore.hasShowData && WordStore.showData.createdAt && WordStore.showData.createdAt.formattedDateWithYear}${WordStore.hasShowData && isPresent(WordStore.showData.createdByUser?.name) ? ` by ${WordStore.showData.createdByUser.name}` : ''}`}
            </fieldset>
            <fieldset>
              <legend>Last Updated</legend>
              {WordStore.hasShowData && WordStore.showData.updatedAt && WordStore.showData.updatedAt.formattedDateWithYear}
            </fieldset>
            {
              this.verb &&
              <fieldset style={{ position: 'relative' }}>
                <legend>Approved</legend>
                <InPlaceCheckbox
                  model='words'
                  field='reviewed2'
                  value={WordStore.showData.reviewed2}
                  id={WordStore.showData.id}
                  icon='thumbs-up'
                  afterChange={this.afterChange}
                >
                  Didactics
                </InPlaceCheckbox>
                {
                  isPresent(WordStore.showData.reviewed2User) &&
                  <ReviewedUser>
                    {WordStore.showData.reviewed2User?.name}
                  </ReviewedUser>
                }
              </fieldset>
            }
          </Grid>
          <Grid columns={[3, 3, 3, 2, 1]}>
            <fieldset>
              <legend>Target</legend>
              <div style={{ display: 'flex' }}>
                {
                  isPresent(WordStore.showData.target) && isPresent(WordStore.showData.target.id) &&
                  <Link
                    to={{ name: 'builder.targets.edit', params: { targetId: WordStore.showData.target.id } }}
                  >
                    <i className='fa fa-chevron-right fa-block' />
                  </Link>
                }
                <InPlaceText
                  model='words'
                  field='target_text'
                  id={WordStore.showData.id}
                  defaultValue={WordStore.showData.targetText}
                  afterChange={this.afterChange}
                />
                {
                  WordStore.showData?.target?.hasGenders && WordStore.showData.target.gender &&
                  <span> ({WordStore.showData.target.gender})</span>
                }
              </div>
              {
                this.number &&
                <div style={{ fontSize: '12px', marginTop: '5px' }}>Displayed as: {WordStore.showData.formattedTargetText}</div>
              }
              {
                this.noun &&
                <div style={{ fontSize: '12px', marginTop: '5px' }}>With Article: {WordStore.showData.targetTextWithDefiniteArticle}</div>
              }
            </fieldset>
            <fieldset>
              <legend>Word Type</legend>
              {
                WordTypeStore.hasIndexData && WordStore.hasShowData &&
                <div>
                  <InPlaceSelect
                    clickToEdit={WordStore.showData.wordType && WordStore.showData.wordType.id}
                    options={WordTypeStore.allForSelect}
                    model='words'
                    field='word_type_id'
                    includeBlank
                    id={WordStore.showData.id}
                    value={WordStore.showData.wordType && WordStore.showData.wordType.id}
                    afterChange={this.afterChange}
                  />
                </div>
              }
              {
                WordStore.hasShowData && WordStore.showData.rootWordType && WordStore.showData.wordType && WordStore.showData.rootWordType.id !== WordStore.showData.wordType.id &&
                <div style={{ fontSize: 12, fontStyle: 'italic' }}>
                  Root: {WordStore.showData.rootWordType.name}
                </div>
              }
              {
                WordStore.hasShowData && isBlank(WordStore.showData.wordType) &&
                <small style={{ background: 'orange', color: 'white', fontSize: 12, fontStyle: 'italic' }}>Remember to use root form</small>
              }
            </fieldset>
            <fieldset>
              <legend>Source List</legend>
              <WordSourceEdit
                word={WordStore.showData}
                onChange={this.afterChange}
                scopedToSentence={false}
              />
            </fieldset>
            {
              (this.noun || this.pronoun) &&
              <fieldset>
                <legend>Gender</legend>
                <InPlaceSelect
                  clickToEdit={WordStore.showData.gender}
                  options={[['masculine', 'masculine'], ['feminine', 'feminine'], ['neuter', 'neuter']]}
                  model='words'
                  field='gender'
                  includeBlank
                  id={WordStore.showData.id}
                  value={WordStore.showData.gender}
                  afterChange={this.afterChange}
                />
                {
                  !WordStore.showData.root &&
                  <div style={{ marginTop: 5, fontSize: 12 }}>
                    Root Word Gender: {WordStore.showData.rootWordGender}
                  </div>
                }
              </fieldset>
            }
            <fieldset>
              <legend>
                Level
                <span
                  data-for='level'
                  data-tip
                  style={{ marginLeft: '5px' }}
                >
                  <i className='fa fa-info-circle' />
                  <ReactTooltip
                    place='right'
                    type='dark'
                    effect='solid'
                    id='level'
                    className='custom-tooltip'
                  >
                    <div>Level can only be modified at the root,</div>
                    <div>and will update all child words' levels.</div>
                  </ReactTooltip>
                </span>
              </legend>
              {
                isPresent(this.levelOptions) &&
                <InPlaceSelect
                  includeBlank
                  options={this.levelOptions}
                  model='words'
                  field='levelId'
                  afterChange={this.afterChange}
                  value={WordStore.showData.levelId}
                  id={WordStore.showData.id}
                  clickToEdit={true}
                  disabled={!WordStore.showData.root}
                />
              }
            </fieldset>
          </Grid>
          <Grid columns={4}>
            <fieldset>
              <legend>Attributes</legend>
              <CheckIfTrue
                value={WordStore.showData.root}
                label='Is Root [Disabled]'
              />
              {
                WordStore.showData.root &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.allowDuplicateRoots}
                    field='allow_duplicate_roots'
                  >
                    Allow Duplicate Roots
                  </InPlaceCheckbox>
                </div>
              }
              {
                this.noun &&
                <InPlaceCheckbox
                  withConfirmation={WordStore.showData.root && !WordStore.showData.plural}
                  left='100px'
                  right='auto'
                  message='This word only exists in the plural form?'
                  confirmationMessageFloatsRight
                  model='words'
                  field='plural'
                  id={WordStore.showData.id}
                  value={WordStore.showData.plural}
                  afterChange={this.afterChange}
                >
                  Is Plural
                </InPlaceCheckbox>
              }
              {
                this.adverb &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.hasAdjectiveForm}
                    field='has_adjective_form'
                  >
                    Has Adjective Form
                  </InPlaceCheckbox>
                </div>
              }
              {
                this.noun && WordStore.showData.plural &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.excludeAsPluralForm}
                    field='exclude_as_plural_form'
                  >
                    <div>Exclude as Plural Form</div>
                    <small>(often -N declined plural forms)</small>
                  </InPlaceCheckbox>
                </div>
              }
              {
                this.noun && !WordStore.showData.plural &&
                <div>
                  {
                    WordStore.showData.pluralNouns.length !== 0 &&
                    <InPlaceCheckbox
                      model='words'
                      id={WordStore.showData.id}
                      value={WordStore.showData.noPlural}
                      field='no_plural'
                      withConfirmation={true}
                      message='Remove plural nouns?'
                      confirmationMessageFloatsRight={true}
                      display='flex'
                      right='auto'
                      left='130px'
                      afterChange={this.afterChange}
                    >
                      Has No Plural
                    </InPlaceCheckbox>
                  }
                  {
                    WordStore.showData.pluralNouns.length === 0 &&
                    <InPlaceCheckbox
                      model='words'
                      id={WordStore.showData.id}
                      value={WordStore.showData.noPlural}
                      field='no_plural'
                    >
                      Has No Plural
                    </InPlaceCheckbox>
                  }

                </div>
              }
              {
                this.noun &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.useParenthesesOnGender}
                    field='use_parentheses_on_gender'
                    afterChange={this.afterChange}
                  >
                    Article Is Hidden (But Exists)
                  </InPlaceCheckbox>
                </div>
              }
              {
                this.noun &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.articleIsHidden}
                    field='article_is_hidden'
                    afterChange={this.afterChange}
                  >
                    Article Is Hidden (Doesn't Exist)
                  </InPlaceCheckbox>
                </div>
              }
              {
                (this.noun || this.verb) && ['FR', 'CA'].includes(WordStore.showData.languageId) &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.articleHasApostrophe}
                    field='article_has_apostrophe'
                    afterChange={this.afterChange}
                  >
                    {this.noun ? 'Article' : 'Pronoun'} Takes Apostrophe
                  </InPlaceCheckbox>
                </div>
              }
              {
                this.noun && (WordStore.showData.languageId === 'FR' || WordStore.showData.languageId === 'ES') &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.articleTakesOppositeGender}
                    field='article_takes_opposite_gender'
                    afterChange={this.afterChange}
                  >
                    Article Takes Opposite Gender
                  </InPlaceCheckbox>
                </div>
              }
              {
                this.verb &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.isPrefix}
                    field='is_prefix'
                  >
                    Is Prefix
                  </InPlaceCheckbox>
                </div>
              }
              {
                this.verb &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.isStem}
                    field='is_stem'
                  >
                    Is Stem
                  </InPlaceCheckbox>
                </div>
              }
              {
                this.number &&
                <div>
                  <InPlaceCheckbox
                    model='words'
                    id={WordStore.showData.id}
                    value={WordStore.showData.isDate}
                    field='is_date'
                    afterChange={this.getWordShow}
                  >
                    Do Not Format (Is Date) <InfoTooltip><div>If this is unchecked, the number will be</div><div>{`automatically formatted: ${Number(WordStore.showData.targetText)?.toLocaleString(WordStore.showData.languageId)}`}</div></InfoTooltip>
                  </InPlaceCheckbox>
                </div>
              }
              {
                WordStore.showData.root &&
                <span
                  data-for='public'
                  data-tip
                  style={{ display: 'flex', fontSize: '14px', alignItems: 'center' }}
                >
                  <CheckIfTrue
                    circle
                    value={WordStore.showData.publicInVocab}
                    width='20px'
                  />
                  Public in dictionary
                  {
                    !isBlank(WordStore.showData.messagePublicInVocab) &&
                    <ReactTooltip
                      place='right'
                      type='dark'
                      effect='solid'
                      id='public'
                      className='custom-tooltip'
                    >
                      {
                        WordStore.showData.messagePublicInVocab.map(item => {
                          return (
                            <div>{item}</div>
                          );
                        })
                      }
                    </ReactTooltip>
                  }
                </span>
              }
            </fieldset>
            <fieldset>
              <legend>Exclude From</legend>
              {
                WordStore.hasShowData &&
                <ExcludeEdit
                  word={WordStore.showData}
                  onChange={this.afterChange}
                />
              }
            </fieldset>
            {/* <fieldset style={{position: 'relative', display: 'flex'}}>
              <legend>
                Minimal pair
                <span
                  data-for='minimal-pair'
                  data-tip
                  style={{marginLeft: '5px'}}
                >
                      <i className='fa fa-info-circle' />
                      <ReactTooltip
                        place='right'
                        type='dark'
                        effect='solid'
                        id='minimal-pair'
                        className='custom-tooltip'
                      >
                        <div>A word that differs only by one sound from this one.</div>
                        <div>Examples: <i>Ofen</i>/<i>offen</i>, <i>cul</i>/<i>cou</i>, <i>peso</i>/<i>piso</i>...</div>
                      </ReactTooltip>
                    </span>
              </legend>
              {
                isPresent(WordStore.showData.genderVariant) &&
                <Link
                  to={{
                    name: 'builder.words.edit',
                    params: {wordId: WordStore.showData.genderVariant.id},
                  }}
                  className='on-click'
                >
                  <i className='fa fa-chevron-right fa-block'/>
                </Link>
              }
              <MinimalPairEdit
                word={WordStore.showData}
                onChange={this.afterChange}
              />
            </fieldset> */}
            <fieldset>
              <legend>Links</legend>
              {
                WordStore.showData && WordStore.showData.root &&
                <div>
                  <a
                    className='underline'
                    style={{ fontSize: 14 }}
                    href={`/vocab/words/${WordStore.showData.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Vocab Page
                  </a>
                </div>
              }
              {
                WordStore.showData && WordStore.showData.vocabTriadConceptId &&
                <div>
                  <a
                    className='underline'
                    style={{ fontSize: 14 }}
                    href={`/builder/concepts/${WordStore.showData.vocabTriadConceptId}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Triad Concept
                  </a>
                </div>
              }
              {
                WordStore.showData && WordStore.showData.vocabTriadDeckId &&
                <div>
                  <a
                    className='underline'
                    style={{ fontSize: 14 }}
                    href={`/builder/decks/${WordStore.showData.vocabTriadDeckId}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Triad Deck
                  </a>
                </div>
              }
              {
                WordStore.showData.forumThreadId &&
                <div>
                  <a
                    className='underline'
                    style={{ fontSize: 14 }}
                    href={`https://www.seedlang.com/discussions/${Constants.VOCAB_FORUM_IDS[SentenceStore.showData.languageId]}/threads/${WordStore.showData.forumThreadId}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Forum
                  </a>
                </div>
              }
            </fieldset>
          </Grid>
          {
            isPresent(WordStore.showData.stems) && (
              <fieldset>
                <legend>Stems</legend>
                <table className='table-wrapper'>
                  <thead>
                    <tr>
                      <th width='20'></th>
                      <th>Target</th>
                      <th>Sentences</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      WordStore.showData.stems.map(item => {
                        return (
                          <tr key={item.id}>
                            <td>
                              <Link
                                to={{ name: 'builder.words.edit', params: { wordId: item.id } }}
                                className='on-click'
                              >
                                <i className='fa fa-chevron-right fa-block' />
                              </Link>
                            </td>
                            <td>
                              <div className='text'>
                                {item.targetText}
                              </div>
                            </td>
                            <td>
                              <div className='text'>
                                {item.sentencesCount}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </fieldset>
            )
          }
          {
            WordStore.showData.hasPrefix && (
              <fieldset>
                <legend>Prefix</legend>
                <table className='table-wrapper'>
                  <thead>
                    <tr>
                      <th width='20'></th>
                      <th>Target</th>
                      <th>Sentences</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Link
                          to={{ name: 'builder.words.edit', params: { wordId: WordStore.showData.prefix.id } }}
                          className='on-click'
                        >
                          <i className='fa fa-chevron-right fa-block' />
                        </Link>
                      </td>
                      <td>
                        <div className='text'>
                          {WordStore.showData.prefix.targetText}
                        </div>
                      </td>
                      <td>
                        <div className='text'>
                          {WordStore.showData.prefix.sentencesCount}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </fieldset>
            )
          }
          {
            WordStore.showData.root &&
            <MeaningsEdit
              word={WordStore.showData}
              afterChange={this.afterChange}
            />
          }
          {
            WordStore.showData.root && this.noun && !WordStore.showData.plural &&
            <Grid columns={2}>
              <fieldset>
                <legend>Plural Nouns</legend>
                <table className='table-wrapper'>
                  <thead>
                    <tr>
                      <th width='20'></th>
                      <th>Target</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      WordStore.showData.pluralNouns.map(item => {
                        return (
                          <tr key={item.id}>
                            <td>
                              <Link
                                to={{ name: 'builder.words.edit', params: { wordId: item.id } }}
                                className='on-click'
                              >
                                <i className='fa fa-chevron-right fa-block' />
                              </Link>
                            </td>
                            <td>
                              <div className='text'>
                                <Link
                                  to={{ name: 'builder.words.edit', params: { wordId: item.id } }}
                                  className='underline'
                                >
                                  {item.targetTextWithDefiniteArticle}
                                  {item.plural && <span> (plur)</span>}
                                  {item.isNoun && !item.plural && <span> (sing)</span>}
                                </Link>
                              </div>
                            </td>
                            <td>
                              <DeleteButton
                                onConfirm={() => this.togglePluralNoun(item.id)}
                                message='Remove from plural nouns?'
                                className='fa-block right'
                              />
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
                <div
                  style={{ marginTop: '5px' }}
                >
                  <WordSearch
                    onSubmit={this.onSelectPlural} // TODO
                    displayField='targetWithWordTypeParentAndSource'
                    placeholder='Find or create another plural noun'
                    filters={{ root: false, root_word_type_id: WordStore.showData.wordType.id, plural: true, language_id: WordStore.showData.languageId }}
                    onCreateValue={this.onCreatePlural} // TODO
                    height='32px'
                    width='100%'
                  />
                </div>
              </fieldset>
              {
                WordStore.showData.languageId === 'DE' &&
                <>
                  <fieldset>
                    <legend>False Plurals</legend>
                    <InPlaceText
                      model='words'
                      field='false_plurals'
                      id={WordStore.showData.id}
                      defaultValue={WordStore.showData.falsePlurals}
                      afterChange={this.afterChange}
                    />
                    <small>Used in Plural Trainer. Separate with comma and no space. Ex: Fernsehsender,Fernsehsendere</small>
                  </fieldset>
                </>
              }
            </Grid>
          }
          <fieldset>
            <legend>False Multiple Choice Options</legend>
            {WordStore.showData.falseMultipleChoiceOptionsEn}
            {
              isPresent(WordStore.showData.falseMultipleChoiceOptions) &&
              <div>
                <GrayButton
                  onClick={() => this.rebuildFalseMultipleChoiceOptions()}
                  className=''
                >
                  Rebuild Options
                </GrayButton>
              </div>
            }
          </fieldset>
          {
            !WordStore.showData.root && isPresent(WordStore.showData.rootWord) && isPresent(WordStore.showData.rootWord.id) && (
              <fieldset>
                <legend>Root Word</legend>
                <table className='table-wrapper'>
                  <thead>
                    <tr>
                      <th width='20'></th>
                      <th>Target</th>
                      <th>Sentences</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Link
                          to={{ name: 'builder.words.edit', params: { wordId: WordStore.showData.rootWord.id } }}
                          className='on-click'
                        >
                          <i className='fa fa-chevron-right fa-block' />
                        </Link>
                      </td>
                      <td>
                        <div className='text'>
                          {WordStore.showData.rootWord.targetText}
                        </div>
                      </td>
                      <td>
                        <div className='text'>
                          {WordStore.showData.rootWord.sentencesCount}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div
                  style={{ marginTop: 5, fontSize: 14, textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={this.clearRoot}
                >
                  Clear Root
                </div>
              </fieldset>
            )
          }
          {
            WordStore.showData.root && (this.adj || this.number) && WordStore.showData.languageId !== 'DE' &&
            <fieldset style={{ position: 'relative', display: 'flex' }}>
              <legend>
                Feminine form
                <span
                  data-for='feminine-form'
                  data-tip
                  style={{ marginLeft: '5px' }}
                >
                  <i className='fa fa-info-circle' />
                  <ReactTooltip
                    place='right'
                    type='dark'
                    effect='solid'
                    id='feminine-form'
                    className='custom-tooltip'
                  >
                    <div>The second form heard in the video. It will be displayed in the dictionary and on cards.</div>
                    <div>It has to be a child word of <i>{WordStore.showData.targetText}</i>.</div>
                    <div>If it doesn't exist, a new child word will be created.</div>
                  </ReactTooltip>
                </span>
              </legend>
              {
                isPresent(WordStore.showData.genderVariant) &&
                <Link
                  to={{
                    name: 'builder.words.edit',
                    params: { wordId: WordStore.showData.genderVariant.id },
                  }}
                  className='on-click'
                >
                  <i className='fa fa-chevron-right fa-block' />
                </Link>
              }
              <GenderVariantEdit
                word={WordStore.showData}
                placeholder='Enter feminine form'
                displayField='targetWithGenderAndSentencesCount'
                onChange={this.afterChange}
              />
            </fieldset>
          }
          {
            isPresent(WordStore.showData.childWords) && (
              <fieldset>
                <legend>Child Words</legend>
                <table className='table-wrapper'>
                  <thead>
                    <tr>
                      <th width='20'></th>
                      <th>Target</th>
                      <th>Sources</th>
                      <th>Sentences</th>
                      <th>Word type</th>
                      <th>Concepts</th>
                      {
                        this.noun &&
                        <th>Plural</th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {
                      WordStore.showData.childWords.filter(item => !item.simple).map(item => {
                        return (
                          <tr key={item.id}>
                            <td>
                              <Link
                                to={{ name: 'builder.words.edit', params: { wordId: item.id } }}
                                className='on-click'
                              >
                                <i className='fa fa-chevron-right fa-block' />
                              </Link>
                            </td>
                            <td>
                              <div className='text'>
                                <Link
                                  to={{ name: 'builder.words.edit', params: { wordId: item.id } }}
                                  className='underline'
                                >
                                  {item.targetTextWithDefiniteArticle}
                                  {item.plural && <span> (plur)</span>}
                                  {item.isNoun && !item.plural && <span> (sing)</span>}
                                </Link>
                              </div>
                            </td>
                            <td>
                              <div className='text'>
                                <i>{item.sourceList}</i>
                              </div>
                            </td>
                            <td>
                              <div className='text'>
                                {item.sentencesCount}
                              </div>
                            </td>
                            <td>
                              <div className='text'>
                                {item.wordTypeName}
                              </div>
                            </td>
                            <td>
                              <div className='text'>
                                {
                                  orderBy(item.concepts, ['name']).map(a => a.name).join(' | ')
                                }
                              </div>
                            </td>
                            {
                              this.noun &&
                              <td>
                                <InPlaceCheckbox
                                  value={WordStore.showData?.pluralNouns.map(item => item.id).includes(item.id)}
                                  onChange={() => this.togglePluralNoun(item.id)}
                                  afterChange={this.afterChange}
                                  id={item.id}
                                  wrapperMargin='3px 3px 0 0'
                                  model='words'
                                />
                              </td>
                            }

                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </fieldset>
            )
          }
          {
            WordStore.showData.root &&
            <SynonymsEdit word={WordStore.showData} afterChange={this.afterChange} />
          }
          <RelatedWordsEdit afterChange={this.afterChange} />
          {
            WordStore.showData.root && this.noun && !WordStore.showData.plural &&
            <fieldset style={{ position: 'relative', display: 'flex' }}>
              <legend>
                Gender Variant
                <span
                  data-for='gender-variant'
                  data-tip
                  style={{ marginLeft: '5px' }}
                >
                  <i className='fa fa-info-circle' />
                  <ReactTooltip
                    place='right'
                    type='dark'
                    effect='solid'
                    id='gender-variant'
                    className='custom-tooltip'
                  >
                    <div>A root noun that is the equivalent to this one in the other gender.</div>
                    <div>Example <i>directrice</i> for <i>directeur</i>/<i>directora</i> for <i>director</i>.</div>
                    <div>It will appear on the dictionary word page as a link.</div>
                  </ReactTooltip>
                </span>
              </legend>
              {
                isPresent(WordStore.showData.genderVariant) &&
                <Link
                  to={{
                    name: 'builder.words.edit',
                    params: { wordId: WordStore.showData.genderVariant.id },
                  }}
                  className='on-click'
                >
                  <i className='fa fa-chevron-right fa-block' />
                </Link>
              }
              <GenderVariantEdit
                word={WordStore.showData}
                onChange={this.afterChange}
              />
            </fieldset>
          }
          {
            WordStore.showData.isVerb && WordStore.showData.root &&
            <fieldset>
              <legend>
                Verb structures
                <InfoTooltip icon='warning'>
                  <div>These are not displayed on Seedlang currently</div>
                </InfoTooltip>
              </legend>
              <table className='table-wrapper'>
                <thead>
                  <tr>
                    <th>{`${Languages[WordStore.showData.languageId]} verb structure`}</th>
                    <th>English translation</th>
                    <th width='30' />
                  </tr>
                </thead>
                <tbody>
                  {
                    WordStore.showData.wordStructures?.map(wordStructure => {
                      return (
                        <tr
                          key={wordStructure.id}
                        >
                          <td>
                            <InPlaceText
                              show={isBlank(wordStructure.text)}
                              submitOnEnter
                              model='word_structures'
                              field='text'
                              defaultValue={wordStructure.text}
                              id={wordStructure.id}
                              type='text'
                              placeholder={`enter a ${Languages[WordStore.showData.languageId]} verb structure`}
                              style={{ margin: '3px' }}
                              updateCallback={this.afterUpdateNewWordStructure}
                            />
                          </td>
                          <td>
                            <InPlaceText
                              show={isBlank(wordStructure.sourceText)}
                              submitOnEnter
                              model='word_structures'
                              field='source_text'
                              defaultValue={wordStructure.sourceText}
                              id={wordStructure.id}
                              type='text'
                              placeholder='enter the English translation'
                              style={{ margin: '3px' }}
                              updateCallback={this.afterUpdateNewWordStructure}
                            />
                          </td>
                          <td>
                            <DeleteButton
                              onConfirm={() => this.deleteWordStructure(wordStructure.id)}
                              message='Delete this word structure?'
                              className='fa-block right'
                            />
                          </td>
                        </tr>
                      );
                    })
                  }
                  {
                    this.showFormToCreateNewWordStructure &&
                    <tr>
                      <td>
                        <input
                          placeholder={`enter a ${Languages[WordStore.showData?.languageId]} verb structure`}
                          ref='text'
                        />
                      </td>
                      <td>
                        <input
                          placeholder='enter the English translation'
                          ref='sourceText'
                        />
                      </td>
                      <td>
                        <SquareButton
                          margin='0'
                          onClick={this.createNewWordStructure}
                        >
                          <i className='fa fa-plus' />
                        </SquareButton>
                      </td>
                    </tr>
                  }
                  {
                    !this.showFormToCreateNewWordStructure &&
                    <tr
                      style={{ cursor: 'pointer' }}
                    >
                      <td
                        colSpan='4'
                        onClick={() => this.showFormToCreateNewWordStructure = true}
                      >
                        <i className='fa fa-plus' style={{ margin: '3px' }} />
                        <Text fontSize='14px'>Create a new verb structure</Text>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
              {
                this.unknownComponents?.length > 0 &&
                <Alert
                  width='100%'
                  margin='5px 0 0 0'
                  showCloseButton
                  onClose={() => this.unknownComponents = []}
                >
                  <span>Word structure invalid. </span>
                  <span style={{ fontStyle: 'italic' }}>{this.unknownComponents.join(', ')}</span>
                  <span>{` ${this.unknownComponents.length > 1 ? 'are' : 'is'} not in the ${Languages[WordStore.showData.languageId]} word structure components. `}</span>
                  <Link
                    className='underline'
                    to={{ name: 'builder.languages.edit', params: { languageId: WordStore.showData.languageId } }}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Edit this list
                  </Link>
                </Alert>
              }
              {
                WordStore.showData.wordStructures?.length > 0 &&
                <div>
                  <Text fontSize='13px'>{`Word structure components for ${Languages[WordStore.showData?.languageId]}: `}</Text>
                  <Text fontSize='13px'><code>{LanguageStore.showData.wordStructureComponents}</code></Text>
                </div>
              }
            </fieldset>
          }
          {
            WordStore.showData.isVerb &&
            <fieldset>
              <legend>Conjugated Verbs Table</legend>
              {
                isPresent(WordStore.showData.conjugatedVerbStrings) &&
                <table className='table-wrapper'>
                  <thead>
                    <tr>
                      <th />
                      <th>Concept</th>
                      {
                        WordStore.showData.languageId === 'FR' &&
                        <th>Fem. <InfoTooltip><div>Display feminine agreement</div></InfoTooltip></th>
                      }
                      <th>Sing 1P</th>
                      <th>Sing 2P</th>
                      <th>Sing 3P</th>
                      <th>Plural 1P</th>
                      <th>Plural 2P</th>
                      <th>Plural 3P</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      WordStore.showData.conjugatedVerbStrings.map(item => {
                        return (
                          <tr key={item.id}>
                            <td>
                              <Copy
                                text={item.id}
                              />
                            </td>
                            <td>
                              {
                                item.concept &&
                                <Link
                                  to={{ name: 'builder.concepts.edit', params: { conceptId: item.concept.id } }}
                                >
                                  {item.concept.name}
                                </Link>
                              }
                            </td>
                            {
                              WordStore.showData.languageId === 'FR' &&
                              <td>
                                <InPlaceCheckbox
                                  value={item.showFeminine}
                                  field='show_feminine'
                                  id={item.id}
                                  model='conjugated_verb_strings'
                                />
                              </td>
                            }
                            <td>
                              <ConjugatedVerbStringGrouping>
                                <InPlaceCheckbox
                                  value={item.hideSingular1p}
                                  field='hide_singular_1p'
                                  afterChange={this.afterChange}
                                  id={item.id}
                                  icon='ban'
                                  wrapperMargin='3px 3px 0 0'
                                  model='conjugated_verb_strings'
                                />
                                <div>
                                  <InPlaceText
                                    defaultValue={item.singular1p}
                                    show={this.editableConjugations}
                                    field='singular_1p'
                                    id={item.id}
                                    color={item.hideSingular1p ? '#CCC' : '#000'}
                                    model='conjugated_verb_strings'
                                  />
                                  {
                                    item.showFeminine &&
                                    <Text italic color='gray' fontSize='12px' lineHeight='14px'>{item.singular1pDisplay}</Text>
                                  }
                                </div>
                              </ConjugatedVerbStringGrouping>
                            </td>
                            <td>
                              <ConjugatedVerbStringGrouping>
                                <InPlaceCheckbox
                                  value={item.hideSingular2p}
                                  field='hide_singular_2p'
                                  afterChange={this.afterChange}
                                  id={item.id}
                                  icon='ban'
                                  wrapperMargin='3px 3px 0 0'
                                  model='conjugated_verb_strings'
                                />
                                <div>
                                  <InPlaceText
                                    defaultValue={item.singular2p}
                                    show={this.editableConjugations}
                                    field='singular_2p'
                                    id={item.id}
                                    color={item.hideSingular2p ? '#CCC' : '#000'}
                                    model='conjugated_verb_strings'
                                  />
                                  {
                                    item.showFeminine &&
                                    <Text italic color='gray' fontSize='12px' lineHeight='14px'>{item.singular2pDisplay}</Text>
                                  }
                                </div>
                              </ConjugatedVerbStringGrouping>
                            </td>
                            <td>
                              <ConjugatedVerbStringGrouping>
                                <InPlaceCheckbox
                                  value={item.hideSingular3p}
                                  field='hide_singular_3p'
                                  afterChange={this.afterChange}
                                  id={item.id}
                                  icon='ban'
                                  wrapperMargin='3px 3px 0 0'
                                  model='conjugated_verb_strings'
                                />
                                <div>
                                  <InPlaceText
                                    defaultValue={item.singular3p}
                                    show={this.editableConjugations}
                                    field='singular_3p'
                                    id={item.id}
                                    color={item.hideSingular3p ? '#CCC' : '#000'}
                                    model='conjugated_verb_strings'
                                  />
                                  {
                                    item.showFeminine &&
                                    <Text italic color='gray' fontSize='12px' lineHeight='14px'>{item.singular3pDisplay}</Text>
                                  }
                                </div>
                              </ConjugatedVerbStringGrouping>
                            </td>
                            <td>
                              <ConjugatedVerbStringGrouping>
                                <InPlaceCheckbox
                                  value={item.hidePlural1p}
                                  field='hide_plural_1p'
                                  afterChange={this.afterChange}
                                  id={item.id}
                                  icon='ban'
                                  wrapperMargin='3px 3px 0 0'
                                  model='conjugated_verb_strings'
                                />
                                <div>
                                  <InPlaceText
                                    defaultValue={item.plural1p}
                                    show={this.editableConjugations}
                                    field='plural_1p'
                                    id={item.id}
                                    color={item.hidePlural1p ? '#CCC' : '#000'}
                                    model='conjugated_verb_strings'
                                  />
                                  {
                                    item.showFeminine &&
                                    <Text italic color='gray' fontSize='12px' lineHeight='14px'>{item.plural1pDisplay}</Text>
                                  }
                                </div>
                              </ConjugatedVerbStringGrouping>
                            </td>
                            <td>
                              <ConjugatedVerbStringGrouping>
                                <InPlaceCheckbox
                                  value={item.hidePlural2p}
                                  field='hide_plural_2p'
                                  afterChange={this.afterChange}
                                  id={item.id}
                                  icon='ban'
                                  wrapperMargin='3px 3px 0 0'
                                  model='conjugated_verb_strings'
                                />
                                <div>
                                  <InPlaceText
                                    defaultValue={item.plural2p}
                                    show={this.editableConjugations}
                                    field='plural_2p'
                                    id={item.id}
                                    color={item.hidePlural2p ? '#CCC' : '#000'}
                                    model='conjugated_verb_strings'
                                  />
                                  {
                                    item.showFeminine &&
                                    <Text italic color='gray' fontSize='12px' lineHeight='14px'>{item.plural2pDisplay}</Text>
                                  }
                                </div>
                              </ConjugatedVerbStringGrouping>
                            </td>
                            <td>
                              <ConjugatedVerbStringGrouping>
                                <InPlaceCheckbox
                                  value={item.hidePlural3p}
                                  field='hide_plural_3p'
                                  afterChange={this.afterChange}
                                  id={item.id}
                                  icon='ban'
                                  wrapperMargin='3px 3px 0 0'
                                  model='conjugated_verb_strings'
                                />
                                <div>
                                  <InPlaceText
                                    defaultValue={item.plural3p}
                                    show={this.editableConjugations}
                                    field='plural_3p'
                                    id={item.id}
                                    color={item.hidePlural3p ? '#CCC' : '#000'}
                                    model='conjugated_verb_strings'
                                  />
                                  {
                                    item.showFeminine &&
                                    <Text italic color='gray' fontSize='12px' lineHeight='14px'>{item.plural3pDisplay}</Text>
                                  }
                                </div>
                              </ConjugatedVerbStringGrouping>
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              }
              <div style={{ display: 'flex' }}>
                <GrayButton
                  style={{ margin: '10px 10px 0 0' }}
                  onClick={() => WordStore.createEmptyVerbConjugations({ ids: { wordId: this.props.params.wordId } }, this.afterChange)}
                >
                  Create Empty Conjugations
                </GrayButton>
                <GrayButton
                  style={{ margin: '10px 10px 0 0' }}
                  onClick={() => {
                    this.editableConjugations = !this.editableConjugations;
                    this.afterChange();
                  }}
                >
                  <i className='fa fa-edit' />
                  Edit conjugations
                </GrayButton>
                <GrayButton
                  style={{ margin: '10px 10px 0 0' }}
                  onClick={() => WordStore.createVerbConjugations({ queryStrings: { site: 'reverso' }, ids: { wordId: this.props.params.wordId } }, this.afterChange)}
                >
                  Scrape Reverso
                </GrayButton>
                {
                  WordStore.showData.languageId === 'DE' &&
                  <GrayButton
                    style={{ margin: '10px 10px 0 0' }}
                    onClick={() => WordStore.createVerbConjugations({ queryStrings: { site: 'woxikon' }, ids: { wordId: this.props.params.wordId } }, this.afterChange)}
                  >
                    Scrape Woxikon
                  </GrayButton>
                }
                {
                  WordStore.showData.languageId === 'DE' &&
                  <GrayButton
                    style={{ margin: '10px 10px 0 0' }}
                    onClick={() => WordStore.createVerbConjugations({ queryStrings: { site: 'verbformen' }, ids: { wordId: this.props.params.wordId } }, this.afterChange)}
                  >
                    Scrape Verbformen
                  </GrayButton>
                }
              </div>
            </fieldset>
          }
          <fieldset>
            <legend>Concepts</legend>
            <table className='table-wrapper'>
              <thead>
                <tr>
                  <th width='20'></th>
                  <th>Concept</th>
                  {
                    WordStore.showData.root &&
                    <th>Show in Dictionary</th>
                  }
                  <th width='20'></th>
                </tr>
              </thead>
              <tbody>
                {
                  WordConceptStore.indexData.map(item => {
                    return (
                      <tr key={item.id}>
                        <td>
                          <Link
                            to={{ name: 'builder.concepts.edit', params: { conceptId: item.id } }}
                            className='on-click'
                          >
                            <i className='fa fa-chevron-right fa-block' />
                          </Link>
                        </td>
                        <td>
                          <div className='text'>
                            {item.name}
                          </div>
                        </td>
                        {
                          WordStore.showData.root &&
                          <td>
                            <CheckIfTrue
                              value={item.showInDictionary}
                            // label=""
                            />
                          </td>
                        }
                        <td>
                          <DeleteButton
                            onConfirm={() => this.onRemoveConcept(item.id)}
                            message='Remove this concept?'
                            className='fa-block right'
                          />
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
            <div className='fieldset-info'>
              <span
                onClick={() => this.showConceptModal = true}
                className='underline'
                style={{ marginRight: 20, fontSize: '14px', display: 'flex', alignItems: 'flex-end' }}
              >
                <i className='fa fa-lightbulb-o' style={{ margin: '5px 5px 0 0' }} /> Edit concepts
              </span>
            </div>
          </fieldset>
          <fieldset>
            <legend>Sentences <InfoTooltip>Only sentences that are public and have a video appear for users.</InfoTooltip></legend>
            <table className='table-wrapper'>
              <thead>
                <tr>
                  <th width='20'></th>
                  <th>Target</th>
                  <th width='50'>Level</th>
                  <th width='50'>Public</th>
                  <th width='50'>Video</th>
                </tr>
              </thead>
              <tbody>
                {
                  WordSentenceStore.indexData.map(item => {
                    return (
                      <tr
                        key={item.id}
                      >
                        <td>
                          <Link
                            to={{ name: 'builder.sentences.edit', params: { sentenceId: item.id } }}
                            className='on-click'
                          >
                            <i className='fa fa-chevron-right fa-block' />
                          </Link>
                        </td>
                        <td>
                          <div className='text'>
                            {item.targetText}
                          </div>
                        </td>
                        <td>
                          <div className='text'>
                            {item.levelAbbreviation}
                          </div>
                        </td>
                        <td>
                          <CheckIfTrue
                            value={item.public}
                          />
                        </td>
                        <td>
                          <CheckIfTrue
                            value={item.hasTargetVideoClip}
                          />
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
            <Paginator
              store={WordSentenceStore}
              ids={{ wordId: this.props.params.wordId }}
            />
          </fieldset>
          <fieldset>
            <legend>Sentence Submissions</legend>
            <SentenceSubmissionIndex
              wordId={this.props.params.wordId}
            />
          </fieldset>
          <fieldset>
            <legend>
              {`Worksheets (${WordStore.showData.worksheetsCount})`}
            </legend>
            {
              WordStore.showData.worksheetsCount > 0 &&
              <table className='table-wrapper'>
                <thead>
                  <tr>
                    <th width='20'></th>
                    <th>Name</th>
                    <th>Website</th>
                    <th width='100'>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    this.worksheets.map(worksheet => {
                      return (
                        <tr key={worksheet.id}>
                          <td>
                            <a href={worksheet.url} className='underline' target='_blank'>
                              <i className='fa fa-chevron-right fa-block' />
                            </a>
                          </td>
                          <td>{worksheet.name}</td>
                          <td>{worksheet.site?.name}</td>
                          <td>{worksheet.publishedAt?.dashesWithYear}</td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            }
            {
              WordStore.showData.worksheetsCount > 2 &&
              <span className='underline' style={{ fontWeight: 'normal', margin: '5px 5px 0 5px', fontSize: '12px' }} onClick={() => this.showWorksheets = !this.showWorksheets}>{`[${this.showWorksheets ? 'hide' : 'show more'}]`}</span>
            }
          </fieldset>
          {
            WordStore.showData.root &&
            <fieldset>
              <legend>Frequency</legend>
              <table>
                <tbody>
                  <tr>
                    <td width='300px'>Overall Frequency Ranking (max: {WordStore.showData.maxFrequencyRanking})</td>
                    <td width='300px'>
                      <InPlaceText
                        model='words'
                        id={WordStore.showData.id}
                        field='frequency_ranking'
                        defaultValue={WordStore.showData.frequencyRanking}
                        afterChange={this.afterChange}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td width='300px'>Frequency Ranking 1 (higher number means higher ranking)</td>
                    <td width='300px'>
                      <InPlaceText
                        model='words'
                        id={WordStore.showData.id}
                        field='frequency_ranking_1'
                        defaultValue={WordStore.showData.frequencyRanking1}
                        afterChange={this.afterChange}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td width='300px'>Frequency Ranking 2</td>
                    <td width='300px'>{WordStore.showData.frequencyRanking2}</td>
                  </tr>
                  <tr>
                    <td width='300px'>Frequency Ranking 3</td>
                    <td width='300px'>{WordStore.showData.frequencyRanking3}</td>
                  </tr>
                  <tr>
                    <td width='300px'>Frequency Score</td>
                    <td width='300px'>{WordStore.showData.frequencyScore}</td>
                  </tr>
                  <tr>
                    <td width='300px'>Frequency Score Adjustment (multiplier, between: 0.001 and 100)</td>
                    <td width='300px'>
                      <InPlaceText
                        model='words'
                        id={WordStore.showData.id}
                        field='frequency_score_adjustment'
                        defaultValue={WordStore.showData.frequencyScoreAdjustment}
                        afterChange={this.afterChange}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </fieldset>
          }
          {
            this.mergeSpinner &&
            <Spinner />
          }
          {
            WordStore.showData.root && !this.mergeSpinner &&
            <GrayButton
              onClick={this.onMergeDuplicateWords}
              style={{ marginTop: '10px' }}
            >
              Merge With Duplicate Word (If Possible)
            </GrayButton>
          }
          {
            isPresent(this.mergeMessage) &&
            <Alert margin='10px 0 0 0'>{this.mergeMessage}</Alert>
          }
          <DeleteButton
            tooltipPosition='right'
            message='Delete Word? This will impact all sentences, sentence submissions, worksheets, etc.'
            confirmationMessageFloatsRight
            onConfirm={this.onDelete}
            disabled={WordStore.showData.worksheetsCount > 0 || WordStore.showData.sentencesCount > 0}
          >
            <GrayButton
              style={{ marginTop: '10px', color: WordStore.showData.worksheetsCount > 0 || WordStore.showData.sentencesCount > 0 ? 'gray' : 'red' }}
              data-tip
              data-for='delete'
            >
              <i className='fa fa-times' style={{ color: WordStore.showData.worksheetsCount > 0 || WordStore.showData.sentencesCount > 0 ? 'gray' : 'red' }} />
              Delete Word
              {
                (WordStore.showData.worksheetsCount > 0 || WordStore.showData.sentencesCount > 0) &&
                <ReactTooltip
                  place='right'
                  type='dark'
                  effect='solid'
                  id='delete'
                  class='custom-tooltip'
                >
                  <div style={{ textTransform: 'none' }}>This word cannot be deleted because it is used in sentences or
                    worksheets.
                  </div>
                </ReactTooltip>
              }
            </GrayButton>
          </DeleteButton>
        </Stack>
      );
    } else {
      return <Spinner />;
    }
  }

  render() {
    return this._content();
  }
}

export default WordEdit;
